import { createInstance, Identify } from "@amplitude/analytics-browser";
import type { BrowserClient, DestinationPlugin, Event as AnalyticsEvent } from "@amplitude/analytics-types";
import type { SessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { logger } from "@octopusdeploy/logging";
import { getResolver } from "@octopusdeploy/octopus-server-client";
import type { AnalyticSession } from "@octopusdeploy/portal-analytics";
import URI from "urijs";
import { anonymizeString } from "~/analytics/anonymizeString";
import { repository } from "~/clientInstance";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { UnsubscribeToThemePaletteTypeChanges } from "~/openTelemetry/context/currentThemeContext";
import { currentThemeContext } from "~/openTelemetry/context/currentThemeContext";
import type { ThemePaletteType } from "~/theme/index";
import { resolveApplicationRootUri } from "~/utils/resolveApplicationRootUri";
export class AmplitudeSession implements AnalyticSession {
    private readonly unsubscribeToThemeChanges: UnsubscribeToThemePaletteTypeChanges;
    public static async create(installationId: string, userId: string, isTelemetryEnabled: boolean, initialTheme: ThemePaletteType, onAnalyticsEventSent: (event: AnalyticsEvent) => void): Promise<AmplitudeSession> {
        const sessionReplayEnabled = isFeatureToggleEnabled("session-replay");
        let amplitudeApiKey = "dummy-token";
        let replayPlugin: SessionReplayPlugin | undefined = undefined;
        const amplitudeInstance: BrowserClient = createInstance();
        if (sessionReplayEnabled && isTelemetryEnabled) {
            const configuredAmplitudeApiKey = await repository.TelemetryConfiguration.amplitudeApiKey();
            if (configuredAmplitudeApiKey) {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                replayPlugin = sessionReplayPlugin() as SessionReplayPlugin;
                amplitudeInstance.add(replayPlugin);
                amplitudeApiKey = configuredAmplitudeApiKey;
            }
        }
        amplitudeInstance.add(createDevToolsPlugin(onAnalyticsEventSent));
        // The real API token is injected in the Telemetry Proxy before forwarding to Amplitude, but Amplitude.JS requires one anyway.
        await amplitudeInstance.init(amplitudeApiKey, {
            defaultTracking: {
                sessions: true,
                attribution: false,
                pageViews: false,
                formInteractions: false,
                fileDownloads: false,
            },
            serverUrl: resolveTelemetryEndpointFrom(window.location),
        }).promise;
        window.amplitude = amplitudeInstance;
        return new AmplitudeSession(amplitudeInstance, replayPlugin, installationId, userId, initialTheme);
    }
    private constructor(private readonly amplitudeInstance: BrowserClient, private readonly replayPlugin: SessionReplayPlugin | undefined, private readonly installationId: string, userId: string, initialTheme: ThemePaletteType) {
        amplitudeInstance.setUserId(userId);
        this.setTheme(initialTheme);
        this.unsubscribeToThemeChanges = currentThemeContext.subscribeToThemePaletteTypeChanges((newThemePaletteType) => this.setTheme(newThemePaletteType));
    }
    track<EventProperties extends {}>(name: string, event: EventProperties): void {
        const sessionReplayProperties = this.replayPlugin?.getSessionReplayProperties() || {};
        this.amplitudeInstance.track(name, { ...event, ...sessionReplayProperties });
    }
    end() {
        this.unsubscribeToThemeChanges();
        try {
            this.amplitudeInstance.reset();
        }
        catch (err) {
            logger.error(err, "(Amplitude) error ending session");
        }
    }
    private setTheme(theme: ThemePaletteType) {
        const identify = new Identify();
        identify.set("Theme", theme);
        this.amplitudeInstance.identify(identify);
    }
    anonymize(value: string): string {
        return anonymizeString(`${this.installationId}-${value}`);
    }
}
function createDevToolsPlugin(onAnalyticsEventSent: (event: AnalyticsEvent) => void): DestinationPlugin {
    return {
        type: "destination",
        name: "octopus-dev-tools",
        execute(event) {
            onAnalyticsEventSent(event);
            return Promise.resolve({
                code: 200,
                event,
                message: "Event forwarded to dev tools",
            });
        },
    };
}
export function resolveTelemetryEndpointFrom(location: Location | string) {
    const baseUri = resolveApplicationRootUri(location);
    const endpointUri = new URI(getResolver(`${baseUri}`)("~/api/telemetry/process"));
    return `${endpointUri.protocol()}://${endpointUri.host()}/${endpointUri.segment().join("/")}`;
}
